/**
 * Tutorial verboso dos componentes de visualização.
 *
 * Este arquivo evita deliberadamente fábricas, loops, map, slice, concat,
 * spreads de configuração e clonagem. Cada exemplo pode ser lido, copiado e
 * alterado sem procurar valores escondidos em outro objeto.
 */

/**
 * Exemplo line.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const lineChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'line',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — line',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Controle dos markers regulares.
  markers: true,

  // Highlights são calculados a partir dos dados atuais.
  highlights: [
    { series: 'Tokyo', type: 'max' },
  ],

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'full',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'full',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo spline.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const splineChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'spline',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — spline',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Sem markers regulares, mantendo somente os highlights automáticos.
  markers: false,
  highlights: [
    { series: 'Sapporo', type: 'avg' },
    { series: 'Tokyo', type: 'max' },
  ],

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'soft',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'soft',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo area.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const areaChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'area',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — area',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'full',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'full',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo sparea.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const stackedPercentageAreaChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'sparea',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — sparea',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'full',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'full',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo bar.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const barChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'bar',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — bar',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'full',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'full',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo column.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const columnChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'column',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — column',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'full',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'full',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo column sem stacked.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const groupedColumnChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'column',

  // Tooltip sem aparência local: o material pertence aos Theme Sources.
  tooltip: {
    enabled: true,
    formatted: '{value}°',
    shared: true,
  },

  // Colunas agrupadas, sem empilhamento.
  stacked: {
    enabled: false,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — column grouped',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'full',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'full',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo pie.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const pieChart = {
  element: 'chart',
  type: 'pie',

  plotOptions: {
    pie: {
      labelMode: 'percentage',
      radius: {
        inner: 58,
        outer: 94,
      },
      caption: {
        show: false,
      },
    },
  },

  stacked: {
    enabled: true,
    mode: 'net',
    gap: 6,
    fixed: 0,
    format: '{value}%',
  },

  tooltip: {
    enabled: true,
    title: '{name}',
    formatted: '{value}%',
    shared: true,
  },

  title: 'Canais de Atendimento — donut',
  subtitle: 'Composição por canal em dois períodos',
  legend: 'right',

  xAxis: {
    title: '',
    labels: {
      format: '{value}',
    },
    grid: 'none',
  },

  yAxis: {
    title: '',
    labels: {},
    grid: 'none',
  },

  series: [
    {
      name: '2025',
      options: {
        dataLabels: {
          format: '{value}%',
          fixed: 0,
        },
      },
      data: [
        { name: 'Webform', value: 32 },
        { name: 'Call', value: 25 },
        { name: 'Email', value: 18 },
        { name: 'Webchat', value: 15 },
        { name: 'Outros', value: 10 },
      ],
    },
    {
      name: '2026',
      options: {
        dataLabels: {
          format: '{value}%',
          fixed: 0,
        },
      },
      data: [
        { name: 'Webform', value: 38 },
        { name: 'Call', value: 22 },
        { name: 'Email', value: 16 },
        { name: 'Webchat', value: 17 },
        { name: 'Outros', value: 7 },
      ],
    },
  ],

  options: {
    width: 760,
    height: 456,
  },

  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo pie clássico, sem furo central.
 *
 * Série única para demonstrar a legenda por fatia e a representação de pizza
 * tradicional sem misturar o conceito com donut multissérie.
 */
const solidPieChart = {
  element: 'chart',
  type: 'pie',

  plotOptions: {
    pie: {
      labelMode: 'percentage',
      radius: {
        inner: 0,
        outer: 92,
      },
      caption: {
        show: false,
      },
    },
  },

  stacked: {
    enabled: false,
  },

  tooltip: {
    enabled: true,
    title: '{name}',
    formatted: '{value}%',
    shared: false,
  },

  title: 'Distribuição por Canal — pie',
  subtitle: 'Exemplo de pizza sem donut',
  legend: 'right',

  xAxis: {
    title: '',
    labels: {
      format: '{value}',
    },
    grid: 'none',
  },

  yAxis: {
    title: '',
    labels: {},
    grid: 'none',
  },

  series: [
    {
      name: 'Participação',
      options: {
        dataLabels: {
          format: '{value}%',
          fixed: 0,
        },
      },
      data: [
        { name: 'Direto', value: 34 },
        { name: 'Parceiros', value: 26 },
        { name: 'Enterprise', value: 18 },
        { name: 'Marketplace', value: 13 },
        { name: 'Outros', value: 9 },
      ],
    },
  ],

  options: {
    width: 760,
    height: 456,
  },

  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo bubble.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const bubbleChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'bubble',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — bubble',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'full',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'full',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo radar.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const radarChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'radar',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'spider',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — radar',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'none',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'none',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo web.
 *
 * A configuração é repetida propositalmente. Quem estiver estudando pode
 * copiar somente este bloco, mudar seus dados e executá-lo isoladamente.
 */
const webChart = {
  element: 'chart',

  // Tipo principal aceito pelo renderizador.
  type: 'web',

  // Opções específicas das famílias de gráficos.
  plotOptions: {
    pie: {
      labelMode: 'value',
      radius: {
        inner: 60,
        outer: 100,
      },
      caption: {
        show: true,
        offset: 'center',
        color: '#cdd1d5',
      },
    },
    bubble: {
      opacity: 0.75,
    },
    radar: {
      filled: false,
      type: 'web',
    },
    tooltip: {
      enabled: true,
      background: 'black',
      border: 'solid white 1px',
      colorText: 'black',
      title: 'Temperatura',
      formatted: '{value}°',
      text: 'Temperatura de {series.name} em {name}: {formatted}',
    },
  },

  // Configuração do cálculo empilhado.
  stacked: {
    enabled: true,
    mode: 'net',
    gap: 5,
    fixed: 1,
    format: '{value}°',
  },

  title: 'Monthly Average Temperature — web',
  subtitle: 'Source: Wikipedia.com',
  legend: 'bottom',

  // Eixo das categorias.
  xAxis: {
    title: 'Months',
    labels: {
      format: '{value}',
    },
    grid: 'none',
  },

  // Eixo dos valores.
  yAxis: {
    title: 'Temperature',
    labels: {},
    grid: 'none',
  },

  // Primeira série completa.
  series: [
    {
      name: 'Sapporo',
      options: {
        dataLabels: {
          format: '{value}°',
        },
      },
      data: [
        { name: 'Jan', value: -2.9, id: 'sapporo_jan' },
        { name: 'Fev', value: -3.6, id: 'sapporo_fev' },
        { name: 'Mar', value: -0.6, id: 'sapporo_mar' },
        { name: 'Abr', value: 4.8, id: 'sapporo_abr' },
        { name: 'Mai', value: 10.2, id: 'sapporo_mai' },
        { name: 'Jun', value: 14.5, id: 'sapporo_jun' },
        { name: 'Jul', value: 17.6, id: 'sapporo_jul' },
        { name: 'Ago', value: 16.5, id: 'sapporo_ago' },
        { name: 'Set', value: 12.0, id: 'sapporo_set' },
        { name: 'Out', value: 6.5, id: 'sapporo_out' },
        { name: 'Nov', value: 2.0, id: 'sapporo_nov' },
        { name: 'Dez', value: -0.9, id: 'sapporo_dez' },
      ],
    },

    // Segunda série completa.
    {
      name: 'Tokyo',
      options: {
        percentage: false,
        stacked: false,
        dataLabels: {
          format: '{value}°',
          fixed: 1,
        },
        color: '#FF6000',
      },
      data: [
        { name: 'Jan', value: 16.0, id: 'tokyo_jan' },
        { name: 'Fev', value: 18.2, id: 'tokyo_fev' },
        { name: 'Mar', value: 23.1, id: 'tokyo_mar' },
        { name: 'Abr', value: 27.9, id: 'tokyo_abr' },
        { name: 'Mai', value: 32.2, id: 'tokyo_mai' },
        { name: 'Jun', value: 36.4, id: 'tokyo_jun' },
        { name: 'Jul', value: 39.8, id: 'tokyo_jul' },
        { name: 'Ago', value: 38.4, id: 'tokyo_ago' },
        { name: 'Set', value: 29.2, id: 'tokyo_set' },
        { name: 'Out', value: 22.0, id: 'tokyo_out' },
        { name: 'Nov', value: 17.8, id: 'tokyo_nov' },
        { name: 'Dez', value: 16.2, id: 'tokyo_dez' },
      ],
    },
  ],

  // O id tokyo_jan da série principal abre estes dados detalhados.
  drilldown: {
    breadcrumbs: 'right',
    series: [
      {
        name: 'tokyo_jan',
        data: [
          { name: '03', value: 16.0, color: 'blue' },
          { name: '07', value: 18.2, color: 'green' },
          { name: '09', value: 17.1, color: 'yellow' },
          { name: '12', value: 14.9, color: 'red' },
          { name: '17', value: 15.2, color: 'cyan' },
          { name: '22', value: 16.4, color: 'purple' },
          { name: '26', value: 15.8, color: 'white' },
        ],
      },
    ],
  },

  // Dimensão do SVG dentro do card.
  options: {
    width: 760,
    height: 456,
  },

  // Impede que o card force a largura do grid pai.
  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo gauge.
 *
 * Este bloco também é autônomo: escala, série, valor e dimensões estão todos
 * declarados aqui, sem depender da configuração de outro gráfico.
 */
const gaugeChart = {
  element: 'chart',
  type: 'gauge',

  plotOptions: {
    fast: {
      min: 0,
      max: 200,
      tick: 50,
      tickDivisor: 10,
      colors: {
        green: 0,
        yellowgreen: 65,
        yellow: 120,
        orange: 150,
        red: 180,
        black: false,
      },
    },
  },

  title: 'Cycle Time — gauge',

  series: [
    {
      name: 'CycleTime',
      unit: 'Km/h',
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0,
        },
      },
      value: 90,
    },
  ],

  options: {
    width: 360,
    height: 190,
  },

  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo speed.
 *
 * Este bloco também é autônomo: escala, série, valor e dimensões estão todos
 * declarados aqui, sem depender da configuração de outro gráfico.
 */
const speedChart = {
  element: 'chart',
  type: 'speed',

  plotOptions: {
    fast: {
      min: 0,
      max: 200,
      tick: 50,
      tickDivisor: 10,
      colors: {
        green: 0,
        yellowgreen: 65,
        yellow: 120,
        orange: 150,
        red: 180,
        black: false,
      },
    },
  },

  title: 'Cycle Time — speed',

  series: [
    {
      name: 'CycleTime',
      unit: 'Km/h',
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0,
        },
      },
      value: 90,
    },
  ],

  options: {
    width: 360,
    height: 190,
  },

  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo vu.
 *
 * Este bloco também é autônomo: escala, série, valor e dimensões estão todos
 * declarados aqui, sem depender da configuração de outro gráfico.
 */
const vuChart = {
  element: 'chart',
  type: 'vu',

  plotOptions: {
    fast: {
      min: 0,
      max: 200,
      tick: 50,
      tickDivisor: 10,
      colors: {
        green: 0,
        yellowgreen: 65,
        yellow: 120,
        orange: 150,
        red: 180,
        black: false,
      },
    },
  },

  title: 'Cycle Time — vu',

  series: [
    {
      name: 'CycleTime',
      unit: 'Km/h',
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0,
        },
      },
      value: 90,
    },
  ],

  options: {
    width: 360,
    height: 190,
  },

  style: {
    minWidth: '0',
  },
};

/**
 * Exemplo bullet.
 *
 * Este bloco também é autônomo: escala, série, valor e dimensões estão todos
 * declarados aqui, sem depender da configuração de outro gráfico.
 */
const bulletChart = {
  element: 'chart',
  type: 'bullet',

  plotOptions: {
    fast: {
      min: 0,
      max: 200,
      tick: 50,
      tickDivisor: 10,
      colors: {
        green: 0,
        yellowgreen: 65,
        yellow: 120,
        orange: 150,
        red: 180,
        black: false,
      },
    },
  },

  title: 'Cycle Time — bullet',

  series: [
    {
      name: 'CycleTime',
      unit: 'Km/h',
      options: {
        dataLabels: {
          format: '{value}',
          fixed: 0,
        },
      },
      value: 90,
    },
  ],

  options: {
    width: 360,
    height: 110,
  },

  style: {
    minWidth: '0',
  },
};

/**
 * Conteúdo completo da aba Gráficos.
 *
 * Os fieldsets separam indicadores, gráficos principais e gráficos rápidos
 * sem retirar nenhuma visualização que existia no tutorial anterior.
 */
export default {
  element: 'group',
  type: 'fieldset',
  style: {
    width: '1600px',
  },
  groups: [
    {
      legend: 'Indicadores resumidos',
      isGrid: true,
      cols: 10,
      content: [
    {
      element: 'miniDisplay',
      label: 'Média de Sapporo',
      value: 6.3,
      unit: '°C',
      aggregate: 'avg',
      scale: { red: -10, orange: 0, yellow: 10, yellowgreen: 20, green: 30 },
    },

    {
      element: 'miniDisplay',
      label: 'Média de Tóquio',
      value: 26.4,
      unit: '°C',
      aggregate: 'avg',
      scale: { red: -10, orange: 0, yellow: 10, yellowgreen: 20, green: 30 },
    },

    {
      element: 'miniDisplay',
      label: 'Pico de Sapporo',
      value: 17.6,
      unit: '°C',
      aggregate: 'max',
      scale: { red: -10, orange: 0, yellow: 10, yellowgreen: 20, green: 30 },
    },

    {
      element: 'miniDisplay',
      label: 'Pico de Tóquio',
      value: 39.8,
      unit: '°C',
      aggregate: 'max',
      scale: { red: -10, orange: 0, yellow: 10, yellowgreen: 20, green: 30 },
    },

    {
      element: 'miniDisplay',
      label: 'Mínima de Sapporo',
      value: -3.6,
      unit: '°C',
      aggregate: 'min',
      scale: { red: -10, orange: 0, yellow: 10, yellowgreen: 20, green: 30 },
    },

    {
      element: 'miniDisplay',
      label: 'Amplitude de Tóquio',
      value: 23.8,
      unit: '°C',
      scale: { red: -10, orange: 0, yellow: 10, yellowgreen: 20, green: 30 },
    },

    {
      element: 'miniDisplay',
      label: 'Meses representados',
      value: 12,
      unit: 'meses',
      aggregate: 'count',
      scale: { red: 0, orange: 2, yellow: 6, yellowgreen: 12, green: 24 },
    },

    {
      element: 'miniDisplay',
      label: 'Pontos plotados',
      value: 24,
      unit: 'pontos',
      aggregate: 'count',
      scale: { red: 0, orange: 2, yellow: 6, yellowgreen: 12, green: 24 },
    },

    {
      element: 'miniDisplay',
      label: 'Séries comparadas',
      value: 2,
      unit: 'séries',
      aggregate: 'count',
      scale: { red: 0, orange: 2, yellow: 6, yellowgreen: 12, green: 24 },
    },

    {
      element: 'miniDisplay',
      label: 'Cycle time atual',
      value: 90,
      unit: 'km/h',
      aggregate: 'avg',
      scale: { green: 0, yellowgreen: 65, yellow: 120, orange: 150, red: 180 },
    },

    {
      element: 'miniDisplay',
      label: 'Faixa utilizada',
      value: 45,
      unit: '%',
      scale: { red: 0, orange: 25, yellow: 50, yellowgreen: 75, green: 90 },
    },

    {
      element: 'miniDisplay',
      label: 'Margem disponível',
      value: 110,
      unit: 'km/h',
      scale: { green: 0, yellowgreen: 65, yellow: 120, orange: 150, red: 180 },
    },

    {
      element: 'miniDisplay',
      label: 'Meta atingida',
      value: 45,
      unit: '%',
      scale: { red: 0, orange: 25, yellow: 50, yellowgreen: 75, green: 90 },
    },
      ],
    },
    {
      legend: 'Gráficos principais',
      isGrid: true,
      cols: 2,
      content: [
        lineChart,
        splineChart,
        areaChart,
        stackedPercentageAreaChart,
        barChart,
        columnChart,
        pieChart,
        bubbleChart,
        radarChart,
        webChart,
        groupedColumnChart,
        solidPieChart,
      ],
    },
    {
      legend: 'Gráficos rápidos',
      isGrid: true,
      cols: 4,
      content: [
        gaugeChart,
        speedChart,
        vuChart,
        bulletChart,
      ],
    },
  ],
};

